import { useState } from "react";
import { Link } from "react-router-dom";
import { Wallet, TrendingDown, Rocket, PlayCircle, ChevronDown, ChevronUp, AlertTriangle } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const BRL = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const fmtDay = (d) => (d ? `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}` : "");

function Stat({ label, value, icon, accent, testid }) {
  return (
    <div className="p-3 rounded-xl border border-border bg-card" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{icon} {label}</div>
      <div className={`text-xl font-bold tracking-tight mt-1 ${accent ? "text-primary" : ""}`}>{value}</div>
    </div>
  );
}

const STATUS = {
  active: { label: "Ativo", cls: "bg-emerald-500 text-white" },
  paused: { label: "Pausado", cls: "bg-amber-500 text-white" },
  completed: { label: "Concluído", cls: "bg-slate-500 text-white" },
};

/** Balance + spending panel shown on top of /ads. `data` = response of GET /ads/spending */
export default function AdsSpendingPanel({ data }) {
  const [showAll, setShowAll] = useState(false);
  if (!data) return null;
  const items = data.items.filter((i) => i.spent_total > 0 || i.budget_type !== "none");
  const history = showAll ? data.history : data.history.slice(0, 6);
  const pausedByBalance = data.items.filter((i) => i.budget_status === "paused" && /saldo/i.test(i.paused_reason || ""));
  return (
    <Card className="p-4 sm:p-5 space-y-4" data-testid="ads-spending-panel">
      {pausedByBalance.length > 0 && (
        <div className="p-3 rounded-xl border border-red-500/40 bg-red-500/10 text-sm flex items-center gap-3 flex-wrap" data-testid="ads-balance-alert">
          <AlertTriangle className="w-4 h-4 text-red-600 shrink-0" />
          <span className="flex-1 min-w-[200px]">
            <b>{pausedByBalance.length === 1 ? "1 anúncio pausado" : `${pausedByBalance.length} anúncios pausados`}</b> por saldo insuficiente: {pausedByBalance.map((i) => i.title).join(", ")}. Adicione saldo e retome a veiculação.
          </span>
          <Button asChild size="sm" className="rounded-full gap-1" data-testid="ads-balance-alert-topup">
            <Link to="/wallet?topup=1"><Wallet className="w-4 h-4" /> Adicionar saldo</Link>
          </Button>
        </div>
      )}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-bold tracking-tight">Saldo & Gastos</h2>
          <p className="text-xs text-muted-foreground">Veiculação cobrada a {BRL(data.cpm_brl)} por 1.000 impressões, direto do saldo da carteira.</p>
        </div>
        <Button asChild size="sm" variant="outline" className="rounded-full gap-1" data-testid="ads-add-balance-btn">
          <Link to="/wallet?topup=1"><Wallet className="w-4 h-4" /> Adicionar saldo</Link>
        </Button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Stat label="Saldo disponível" value={BRL(data.wallet_balance)} icon={<Wallet className="w-3 h-3" />} accent testid="ads-wallet-balance" />
        <Stat label="Total investido" value={BRL(data.totals.spent)} icon={<TrendingDown className="w-3 h-3" />} testid="ads-total-spent" />
        <Stat label="Em veiculação" value={BRL(data.totals.delivery)} icon={<PlayCircle className="w-3 h-3" />} testid="ads-total-delivery" />
        <Stat label="Em impulsos" value={BRL(data.totals.boost)} icon={<Rocket className="w-3 h-3" />} testid="ads-total-boost" />
      </div>

      {items.length > 0 && (
        <div className="overflow-x-auto" data-testid="ads-spending-by-ad">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">Gastos por anúncio / campanha</div>
          <table className="w-full text-xs">
            <thead className="text-muted-foreground">
              <tr className="text-left border-b border-border">
                <th className="py-1.5 pr-2 font-medium">Anúncio</th>
                <th className="py-1.5 pr-2 font-medium">Orçamento</th>
                <th className="py-1.5 pr-2 font-medium text-right">Veiculação</th>
                <th className="py-1.5 pr-2 font-medium text-right">Impulsos</th>
                <th className="py-1.5 font-medium text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {items.map((i) => {
                const st = STATUS[i.budget_status];
                return (
                  <tr key={i.ad_id} className="border-b border-border/60 last:border-0" data-testid={`ads-spend-row-${i.ad_id}`}>
                    <td className="py-1.5 pr-2 font-medium max-w-[160px] truncate">{i.title}</td>
                    <td className="py-1.5 pr-2">
                      {i.budget_type === "none" ? <span className="text-muted-foreground">—</span> : (
                        <span className="inline-flex items-center gap-1 flex-wrap">
                          {i.budget_type === "daily" ? `${BRL(i.daily_budget)}/dia` : `${BRL(i.total_budget)} total`}
                          {st && <Badge className={`${st.cls} text-[9px] px-1.5 py-0`} title={i.paused_reason || ""}>{st.label}</Badge>}
                        </span>
                      )}
                    </td>
                    <td className="py-1.5 pr-2 text-right tabular-nums">{BRL(i.spent_delivery)}</td>
                    <td className="py-1.5 pr-2 text-right tabular-nums">{BRL(i.spent_boost)}</td>
                    <td className="py-1.5 text-right tabular-nums font-semibold">{BRL(i.spent_total)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div data-testid="ads-spending-history">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">Histórico de gastos</div>
        {data.history.length === 0 ? (
          <p className="text-xs text-muted-foreground" data-testid="ads-spending-history-empty">Nenhum gasto ainda. Defina um orçamento ou impulsione um anúncio para começar.</p>
        ) : (
          <ul className="divide-y divide-border/60">
            {history.map((h) => (
              <li key={h.log_id || `${h.ad_id}-${h.day}-${h.type}`} className="py-1.5 flex items-center gap-3 text-xs" data-testid={`ads-spend-log-${h.log_id}`}>
                <span className="text-muted-foreground tabular-nums w-20 shrink-0">{fmtDay(h.day)}</span>
                <Badge variant="outline" className="text-[9px] px-1.5 py-0 shrink-0 gap-1">
                  {h.type === "boost" ? <Rocket className="w-2.5 h-2.5" /> : <PlayCircle className="w-2.5 h-2.5" />}
                  {h.type === "boost" ? "Impulso" : "Veiculação"}
                </Badge>
                <span className="flex-1 min-w-0 truncate">{h.title}{h.note ? <span className="text-muted-foreground"> · {h.note}</span> : null}</span>
                <span className="font-semibold tabular-nums shrink-0">-{BRL(h.amount)}</span>
              </li>
            ))}
          </ul>
        )}
        {data.history.length > 6 && (
          <Button variant="ghost" size="sm" className="mt-1 h-7 text-xs gap-1" onClick={() => setShowAll((v) => !v)} data-testid="ads-spending-toggle">
            {showAll ? <><ChevronUp className="w-3 h-3" /> Ver menos</> : <><ChevronDown className="w-3 h-3" /> Ver todos ({data.history.length})</>}
          </Button>
        )}
      </div>
    </Card>
  );
}
